import React, { useContext, useEffect, useRef, useState } from "react";

import { BiMicrophone } from "react-icons/bi";
import Cookies from "js-cookie";
import { FaPause, FaPlay, FaTrash } from "react-icons/fa";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { IoIosSend } from "react-icons/io";
import { ConversationsContext } from "../context/ConversationsContext";
import { GiSoundWaves } from "react-icons/gi";
import { isTelegramWebApp } from "../utils/telegramDetect";
import { captureRefreshedTokenFromFetch } from "../utils/tokenRefresh";

const HR_API_BASE_URL = "https://api.oneplace.hr";

// Kept for the life of the page when sessionStorage is unavailable (blocked in some
// in-app browsers and iframes). It used to fall back to null, and nearly every CHAT row in
// job_views arrives with no session — which also switches off the server's per-session
// de-duplication for anonymous views.
let memoryChatSessionId = null;

const getChatSessionId = () => {
  try {
    let sid = sessionStorage.getItem('chat_view_session');
    if (!sid) {
      sid = memoryChatSessionId || `${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
      sessionStorage.setItem('chat_view_session', sid);
    }
    memoryChatSessionId = sid;
    return sid;
  } catch {
    if (!memoryChatSessionId) {
      memoryChatSessionId = `${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
    }
    return memoryChatSessionId;
  }
};

const VoiceMessage = ({ file }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [, setDuration] = useState("0:00");
  const [displayDuration] = useState("0:00");

  const formatDuration = (seconds) => {
    if (!seconds || !isFinite(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  // useEffect(() => {
  //   if (!file) return;

  //   const fetchDuration = async () => {
  //     try {
  //       let blob;

  //       if (typeof file === "string") {
  //         const response = await fetch(file);
  //         if (!response.ok)
  //           throw new Error(`HTTP error! status: ${response.status}`);
  //         blob = await response.blob();
  //       } else if (file instanceof Blob) {
  //         blob = file;
  //       } else {
  //         console.error("Unsupported file format");
  //         return;
  //       }

  //       const durationFromBlob = await getAudioDurationFromBlob(blob);
  //       const calculatedDuration =
  //         durationFromBlob || calculateDurationFallback([blob]);
  //       setDisplayDuration(formatDuration(calculatedDuration));
  //     } catch (error) {
  //       console.error("Error", error);
  //     }
  //   };

  //   fetchDuration();
  // }, [file]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => {
      if (isFinite(audio.currentTime)) {
        setDuration(formatDuration(audio.currentTime));
      }
    };

    if (isPlaying) {
      audio.addEventListener("timeupdate", updateTime);
      return () => audio.removeEventListener("timeupdate", updateTime);
    }
  }, [isPlaying]);

  return (
    <div className="text-white flex items-center justify-between space-x-1 max-w-xs">
      <button
        onClick={togglePlay}
        className="p-2 bg-[#000] text-white rounded-full"
      >
        {isPlaying ? <FaPause size={14} /> : <FaPlay size={14} />}
      </button>
      <span className="text-sm no-underline">
        {/* {duration} */}
        <GiSoundWaves className="text-4xl" />

        {/* / {displayDuration} */}
      </span>

      <audio
        ref={audioRef}
        src={typeof file === "string" ? file : file?.url}
        onEnded={() => {
          setIsPlaying(false);
          setDuration(displayDuration);
        }}
        style={{ display: "none" }}
      />
    </div>
  );
};

export default function Chat({ isSidebarOpen }) {
  const { cId, jId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [messageText, setMessageText] = useState("");
  const [pendingAttachment, setPendingAttachment] = useState(null);
  const [voiceMsg, setVoiceMsg] = useState(null);
  const [history, setHistory] = useState(null);
  const [initError, setInitError] = useState(false);

  const [recordingTime, setRecordingTime] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [audioChunks, setAudioChunks] = useState([]);
  const mediaRecorderRef = useRef(null);
  const fileInputRef = useRef(null);

  const chatContainerRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [loadingChat, setLoadingChat] = useState(false);

  const textareaRef = useRef(null);
  const abortControllerRef = useRef(null);

  const {
    setCurrentConversationInfo,
    conversations,
    loading: conversationsLoading,
  } = useContext(ConversationsContext);

  useEffect(() => {
    // Clear any stale error from a previous run. Without this, the error set by
    // the bare-/chat branch below sticks forever: React reuses this same Chat
    // instance when ConversationsContext redirects /chat -> /chat/{cId}/{jId}
    // (both routes render an identical tree), so the effect re-runs with valid
    // ids but initError is never reset, leaving the error screen up on success.
    setInitError(false);

    // No route params (landed on bare /chat). ConversationsContext redirects to
    // /chat/{cId}/{jId} once the conversation list loads, so calling the API
    // with undefined ids here would create orphaned "undefined-undefined"
    // conversations. Stay in the loading state while that redirect is pending;
    // only show an error once the list has loaded and there is nothing to open.
    if (!cId || !jId) {
      if (conversationsLoading || conversations.length > 0) {
        setLoading(true);
      } else {
        setLoading(false);
        setInitError(true);
      }
      return;
    }
    setLoading(true);

    // On first login we navigate to /chat/{cId}/{jId} immediately, often before
    // the backend has finished creating the candidate/conversation record. The
    // first chat/init can therefore fail transiently (404/500) and resolve a
    // moment later. Retry a few times with a short backoff before showing the
    // hard error screen so that race doesn't flash an error at the candidate.
    let cancelled = false;
    let retryTimer;
    const MAX_RETRIES = 3;
    const RETRY_DELAY_MS = 1000;

    const doInit = (attempt) => {
      const token = Cookies.get("chatToken");
      axios
        .get(
          `https://aichatbot-326159028339.asia-southeast1.run.app/chat/init/${cId}/${jId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((data) => {
          if (cancelled) return;
          setHistory(data.data);
          // Store current conversation info for header display
          if (data.data) {
            setCurrentConversationInfo({
              companyId: cId,
              jobId: jId,
              companyName: data.data.companyName,
              companyPhoto: data.data.companyPhoto,
              aichatbotAvatarUrl: data.data.aichatbotAvatarUrl,
              jobName: data.data.jobName,
              status: data.data.status,
            });
          }
          // Record job view for analytics (fire-and-forget)
          // Pass candidateId explicitly from token claims, because chatToken may be
          // an aichatbot token (for Telegram users) that hrSystemServer can't parse.
          // Use sourceChannel from URL query params if available (e.g., ?sourceChannel=facebook
          // from career page), otherwise fall back to "telegram" or "career_page".
          if (jId && jId !== "0") {
            const urlSourceChannel = searchParams.get("sourceChannel");
            const sourceChannel = isTelegramWebApp() ? "telegram" : (urlSourceChannel || "career_page");
            const viewParams = { assessmentId: jId, platform: "CHAT", sourceChannel, sessionId: getChatSessionId() };
            try {
              if (token) {
                const payload = JSON.parse(atob(token.split(".")[1]));
                if (payload.id) viewParams.candidateId = payload.id;
              }
            } catch (e) { /* ignore decode errors */ }
            axios.post(`${HR_API_BASE_URL}/v1/job-view/record`, null, {
              params: viewParams,
            }).catch(() => {});
          }
          setLoading(false);
        })
        .catch((err) => {
          console.log(err);
          if (cancelled) return;

          // If token is expired/invalid, clear it and redirect to login
          if (err.response?.status === 401) {
            setLoading(false);
            Cookies.remove("chatToken");
            navigate("/login", { replace: true });
            return;
          }

          if (err.message === "Network Error" && !err.response) {
            setLoading(false);
            window.location.reload(true);
            return;
          }

          // Transient failure (e.g. first-login race where the conversation
          // isn't ready yet): retry with backoff before giving up.
          if (attempt < MAX_RETRIES) {
            retryTimer = setTimeout(() => {
              if (!cancelled) doInit(attempt + 1);
            }, RETRY_DELAY_MS);
            return;
          }

          // Show error state only after retries are exhausted.
          setLoading(false);
          setInitError(true);
        });
    };

    doInit(0);

    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
    };
  }, [
    cId,
    jId,
    conversationsLoading,
    conversations.length,
    setCurrentConversationInfo,
    searchParams,
    navigate,
  ]);

  useEffect(() => {
    let timer;

    if (isRecording) {
      timer = setInterval(() => {
        setRecordingTime((prevTime) => prevTime + 1);
      }, 1000);
    } else {
      setRecordingTime(0);
    }

    return () => clearInterval(timer);
  }, [isRecording]);

  useEffect(() => {
    if (voiceMsg && voiceMsg.url) {
      sendMessage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceMsg]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const secs = (seconds % 60).toString().padStart(2, "0");
    return `${minutes}:${secs}`;
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];

      recorder.ondataavailable = (e) => {
        chunks.push(e.data);
      };

      mediaRecorderRef.current = recorder;
      setAudioChunks(chunks);
      recorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error("Failed to start recording:", err);
    }
  };

  const stopRecordingAndSend = async () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) return;

    await new Promise((resolve) => {
      recorder.onstop = resolve;
      recorder.stop();
    });

    setIsRecording(false);

    const audioBlob = new Blob(audioChunks);

    try {
      await sendAudio(audioBlob);
    } catch (error) {
      console.error("Error sending voice message: ", error);
    }

    mediaRecorderRef.current = null;
  };

  const sendAudio = async (audioBlob) => {
    const formData = new FormData();
    const token = Cookies.get("chatToken");

    formData.append("audio", audioBlob, "recording.mp3");

    try {
      const response = await fetch(
        "https://aichatbot-326159028339.asia-southeast1.run.app/chat/conversation/voice",
        {
          method: "POST",
          body: formData,
          credentials: "include",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      captureRefreshedTokenFromFetch(response);

      const result = await response.json();

      if (response.ok) {
        setVoiceMsg({ data: result, url: result.audioUrl });
        setHistory((prev) => {
          const updatedTextResponse = [
            ...prev.textResponse,
            {
              user: result.audioUrl,
              voice: true,
              chatbot: null,
            },
          ];
          return { ...prev, textResponse: updatedTextResponse };
        });
        sendMessage();
      }
    } catch (error) {
      console.error("Error uploading audio:", error);
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stream
        .getTracks()
        .forEach((track) => track.stop());
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }
    setAudioChunks([]);
    setIsRecording(false);
  };

  // Select an image; just preview. Actual upload happens on Send.
  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      // Option A: only images are supported for now.
      event.target.value = "";
      return;
    }
    const previewUrl = URL.createObjectURL(file);
    setPendingAttachment({ file, name: file.name, type: file.type, url: previewUrl });
    event.target.value = "";
  };

  // Upload + attach image. The attach endpoint now runs the full AI
  // pipeline, so we append BOTH the user's image bubble AND the bot's
  // follow-up question/action to the chat.
  const sendImage = async (attachment) => {
    const token = Cookies.get("chatToken");
    const formData = new FormData();
    formData.append("image", attachment.file, attachment.name || "image");

    const uploadRes = await fetch(
      "https://aichatbot-326159028339.asia-southeast1.run.app/chat/conversation/image",
      {
        method: "POST",
        body: formData,
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    captureRefreshedTokenFromFetch(uploadRes);
    const uploadData = await uploadRes.json();
    if (!uploadRes.ok) throw new Error(uploadData?.error || "Image upload failed");

    const attachRes = await axios.post(
      "https://aichatbot-326159028339.asia-southeast1.run.app/chat/conversation/image/attach",
      {
        imageUrl: uploadData.imageUrl,
        imagePath: uploadData.imagePath,
        mimeType: uploadData.mimeType,
        caption: "",
        companyId: cId,
        jobId: jId,
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const fileInfo = attachRes.data?.fileInfo || {
      imagePath: uploadData.imagePath,
      mimeType: uploadData.mimeType,
      imageUrl: uploadData.imageUrl,
    };
    const botReply = attachRes.data?.textResponse || null;

    setHistory((prev) => {
      const next = [
        ...prev.textResponse,
        {
          user: null,
          chatbot: null,
          fileInfo,
          timestamp: new Date().toISOString(),
        },
      ];
      if (botReply) {
        next.push({ user: null, chatbot: botReply, timestamp: new Date().toISOString() });
      }
      return { ...prev, textResponse: next };
    });
  };

  const sendMessage = async () => {
    const text = messageText.trim();
    if (!text && !voiceMsg && !pendingAttachment) return;
    setLoadingChat(true);

    // Handle pending image attachment first (Option A: no AI call).
    // If there is also text or voice, we still send them through the normal
    // /conversation flow afterwards so the AI continues the interview.
    if (pendingAttachment) {
      const attachment = pendingAttachment;
      setPendingAttachment(null);
      try {
        await sendImage(attachment);
      } catch (error) {
        console.error("Error sending image:", error);
        setHistory((prev) => ({
          ...prev,
          textResponse: [
            ...prev.textResponse,
            { user: null, chatbot: "Failed to send image. Please try again." },
          ],
        }));
      } finally {
        if (attachment.url?.startsWith("blob:")) URL.revokeObjectURL(attachment.url);
      }
      if (!text && !voiceMsg) {
        setLoadingChat(false);
        return;
      }
    }

    const textarea = textareaRef.current;
    if (textarea) {
      textarea.rows = 1;
      textarea.style.height = "auto";
      textarea.selectionStart = 0;
      textarea.selectionEnd = 0;
      // Dismiss keyboard on mobile after sending message
      textarea.blur();
    }

    const token = Cookies.get("chatToken");

    // Create AbortController for request cancellation
    abortControllerRef.current = new AbortController();
    const timeoutId = setTimeout(() => abortControllerRef.current?.abort(), 60000); // 60 second timeout

    try {
      if (voiceMsg !== null) {
        const newMessage = {
          sender: "me",
          time: new Date().toISOString(),
          text: {
            text: voiceMsg.url,
            companyId: cId,
            jobId: jId,
          },
          file: null,
        };

        const messageResponse = await axios.post(
          "https://aichatbot-326159028339.asia-southeast1.run.app/chat/conversation",
          newMessage.text,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            signal: abortControllerRef.current.signal,
            timeout: 60000,
          }
        );

        const messageData = messageResponse.data;

        setHistory((prev) => {
          const updatedTextResponse = [
            ...prev.textResponse,
            { user: null, chatbot: messageData.textResponse },
          ];
          return { ...prev, textResponse: updatedTextResponse };
        });

        setVoiceMsg(null);
      } else if (text) {
        const newMessage = {
          sender: "me",
          time: new Date().toISOString(),
          text: {
            text,
            companyId: cId,
            jobId: jId,
          },
          file: null,
        };

        setMessageText("");

        setHistory((prev) => {
          const updatedTextResponse = [
            ...prev.textResponse,
            { user: text, chatbot: null },
          ];
          return { ...prev, textResponse: updatedTextResponse };
        });

        const response = await axios.post(
          "https://aichatbot-326159028339.asia-southeast1.run.app/chat/conversation",
          newMessage.text,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            signal: abortControllerRef.current.signal,
            timeout: 60000,
          }
        );

        const data = response.data;

        setHistory((prev) => {
          const updatedTextResponse = [
            ...prev.textResponse,
            { user: null, chatbot: data.textResponse },
          ];
          return { ...prev, textResponse: updatedTextResponse };
        });
      }
    } catch (error) {
      if (axios.isCancel(error) || error.name === 'AbortError') {
        console.log("Request cancelled or timed out");
        // Show user-friendly error message
        setHistory((prev) => {
          const updatedTextResponse = [
            ...prev.textResponse,
            { user: null, chatbot: "Request timed out. Please try again." },
          ];
          return { ...prev, textResponse: updatedTextResponse };
        });
      } else {
        console.error("Error sending message: ", error);
        // Show error message to user
        setHistory((prev) => {
          const updatedTextResponse = [
            ...prev.textResponse,
            { user: null, chatbot: "Sorry, there was an error. Please try again." },
          ];
          return { ...prev, textResponse: updatedTextResponse };
        });
      }
    } finally {
      clearTimeout(timeoutId);
      setLoadingChat(false);
    }
  };

  // Cleanup: Cancel ongoing requests when component unmounts or user refreshes
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (chatContainerRef.current) {
      const isMobile = window.innerWidth <= 768;

      if (isMobile) {
        // Scroll to the most recent user message
        chatContainerRef.current.scrollTop =
          chatContainerRef.current.scrollHeight;
      } else {
        // Scroll to the most recent user message
        chatContainerRef.current.scrollTop =
          chatContainerRef.current.scrollHeight;
      }
    }
  }, [history?.textResponse, loadingChat]);

  return (
    <div className="w-full flex flex-col h-full  relative">
      <div
        className={`flex-1 overflow-y-scroll p-4 space-y-4 md:space-y-8 pb-28 ${
          isSidebarOpen ? "md:px-[10%]" : "md:px-[20%]"
        } px-6`}
        ref={chatContainerRef}
      >
        {initError ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center space-y-4">
              <p className="text-gray-500">Алдаа гарлаа. Дахин оролдоно уу.</p>
              <button
                onClick={() => {
                  setInitError(false);
                  setLoading(true);
                  const token = Cookies.get("chatToken");
                  axios
                    .get(
                      `https://aichatbot-326159028339.asia-southeast1.run.app/chat/init/${cId}/${jId}`,
                      { headers: { Authorization: `Bearer ${token}` } }
                    )
                    .then((data) => {
                      setHistory(data.data);
                      if (data.data) {
                        setCurrentConversationInfo({
                          companyId: cId,
                          jobId: jId,
                          companyName: data.data.companyName,
                          companyPhoto: data.data.companyPhoto,
                          aichatbotAvatarUrl: data.data.aichatbotAvatarUrl,
                          jobName: data.data.jobName,
                          status: data.data.status,
                        });
                      }
                      setLoading(false);
                    })
                    .catch((err) => {
                      if (err.response?.status === 401) {
                        Cookies.remove("chatToken");
                        navigate("/login", { replace: true });
                        return;
                      }
                      setLoading(false);
                      setInitError(true);
                    });
                }}
                className="px-6 py-2 bg-black text-white rounded-full hover:bg-gray-800 transition"
              >
                Дахин оролдох
              </button>
            </div>
          </div>
        ) : loading ? (
          <>
            {Array.from({ length: 5 }).map((_, index) => (
              <div
                key={index}
                className={`flex items-end gap-3 ${
                  index % 2 === 0 ? "justify-start" : "justify-end"
                }`}
              >
                {index % 2 === 0 && (
                  <div className="w-8 h-8">
                    <Skeleton circle width={32} height={32} />
                  </div>
                )}

                <div
                  className={`flex-1 space-y-2 max-w-[70%] md:max-w-md px-4 py-2 rounded-3xl ${
                    index % 2 === 0 ? "bg-white" : "bg-[#000] text-white"
                  }`}
                >
                  <Skeleton width="80%" height={16} />
                  <Skeleton width="60%" height={16} />
                  <Skeleton width="40%" height={16} />
                </div>

                {index % 2 !== 0 && (
                  <div className="w-8 h-8">
                    <Skeleton circle width={32} height={32} />
                  </div>
                )}
              </div>
            ))}
          </>
        ) : (
          history?.textResponse
            ?.filter((msg) => msg.chatbot || msg.user || msg.fileInfo?.imageUrl)
            .map((msg, idx) => {
              const sender = msg.chatbot ? "them" : "me";
              const isVoiceMessage = msg.user?.startsWith(
                "https://storage.googleapis.com/oneplace-voice/voice-uploads/"
              );
              const imageUrl = msg.fileInfo?.imageUrl;

              return (
                <div
                  key={idx}
                  className={`flex items-end gap-3 ${
                    sender === "me" ? "justify-end" : "justify-start"
                  } group`}
                >
                  {sender === "them" && (
                    <div className="w-8 h-8 rounded-full relative border flex items-center justify-center bg-[#fff]">
                      <img
                        src={history?.aichatbotAvatarUrl || "/chatbot.jpeg"}
                        alt={history.name}
                        className="w-full h-full object-cover rounded-full"
                      />
                      {history.status === "Active now" && (
                        <div className="w-3 h-3 rounded-full bg-[#fff] flex items-center justify-center absolute bottom-0 right-0">
                          <div className="bg-green-400 w-2 h-2 rounded-full"></div>
                        </div>
                      )}
                    </div>
                  )}

                  <div
                    className={`relative max-w-[70%] md:max-w-md px-4 py-2 rounded-3xl shadow-custom text-sm transition-colors duration-300 flex flex-col  ${
                      sender === "me"
                        ? "bg-[#000] text-white"
                        : "bg-white text-[#000]"
                    }`}
                  >
                    {msg.chatbot && (
                      <p className="whitespace-pre-wrap break-words">
                        {msg.chatbot}
                      </p>
                    )}
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt="uploaded"
                        className="max-w-xs rounded-lg"
                      />
                    ) : isVoiceMessage ? (
                      <VoiceMessage file={msg.user} />
                    ) : (
                      msg.user && (
                        <p className="whitespace-pre-wrap break-words">
                          {msg.user}
                        </p>
                      )
                    )}
                    {msg.file && (
                      <div className="mt-1">
                        {msg.file.type.startsWith("image/") && (
                          <img
                            src={msg.file.url}
                            alt="uploaded"
                            className="max-w-xs rounded-lg"
                          />
                        )}
                        {msg.file.type.startsWith("video/") && (
                          <video controls className="max-w-xs rounded-lg">
                            <source src={msg.file.url} type={msg.file.type} />
                            Your browser does not support the video tag.
                          </video>
                        )}
                        {!msg.file.type.startsWith("image/") &&
                          !msg.file.type.startsWith("video/") && (
                            <a
                              href={msg.file.url}
                              download={msg.file.name}
                              className="underline"
                            >
                              {msg.file.name}
                            </a>
                          )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
        )}
        {loadingChat && (
          <div className={`flex items-end gap-3 justify-start pt-4 mb-20`}>
            <div className="w-8 h-8 rounded-full relative border flex items-center justify-center bg-[#fff] shadow-xl">
              <img
                src={history?.aichatbotAvatarUrl || "/chatbot.jpeg"}
                alt={history.name}
                className="w-full h-full object-cover rounded-full"
              />
            </div>

            <div
              className={`flex space-y-2 px-4 py-3 rounded-3xl bg-white shadow-custom`}
            >
              <div className="flex items-center space-x-1 mt-1">
                {[0, 1, 2].map((dot) => (
                  <span
                    key={dot}
                    className="w-2.5 h-2.5 bg-gray-500 rounded-full animate-dot-bounce-high"
                    style={{
                      animationDelay: `${dot * 0.2}s`,
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <div
        className={`p-4 border flex-shrink-0 bg-[#fff] shadow-xl  ${
          isSidebarOpen ? "md:w-[80%]" : "md:w-[60%]"
        } w-[90%] rounded-3xl bg-none absolute bottom-4 left-1/2 transform -translate-x-1/2`}
      >
        {pendingAttachment && (
          <div className="mb-2 flex items-center gap-2 p-2 border border-gray-300 rounded-lg bg-white">
            {pendingAttachment.type.startsWith("image/") && (
              <img
                src={pendingAttachment.url}
                alt="preview"
                className="w-16 h-16 object-cover rounded"
              />
            )}
            {pendingAttachment.type.startsWith("video/") && (
              <video className="w-16 h-16 rounded" controls>
                <source
                  src={pendingAttachment.url}
                  type={pendingAttachment.type}
                />
              </video>
            )}
            {!pendingAttachment.type.startsWith("image/") &&
              !pendingAttachment.type.startsWith("video/") && (
                <p className="text-sm">{pendingAttachment.name}</p>
              )}
            <button
              onClick={() => setPendingAttachment(null)}
              className="text-red-500 text-sm hover:underline"
            >
              ❌
            </button>
          </div>
        )}

        {isRecording ? (
          <div className="flex items-center justify-between w-full">
            <button
              onClick={cancelRecording}
              className="text-red-400 text-base md:text-2xl p-2"
              title="Cancel Recording"
            >
              <FaTrash />
            </button>
            <div className="flex-1 flex flex-col items-center justify-center">
              {/* Timer */}
              <div className="text-red-500 font-semibold text-sm md:text-lg">
                {formatTime(recordingTime)}
              </div>
              {/* Moving Dots Animation */}
              <div className="flex items-center gap-1 mt-1">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-red-500 rounded-full animate-bounce [animation-delay:.2s]"></div>
                <div className="w-2 h-2 bg-red-500 rounded-full animate-bounce [animation-delay:.4s]"></div>
              </div>
            </div>
            <button
              onClick={stopRecordingAndSend}
              className="bg-[#000] text-white px-4 py-2 rounded-full ml-2 md:text-base text-sm"
            >
              Send
            </button>
          </div>
        ) : (
          <div className={`flex items-center gap-2`}>
            <>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleFileUpload}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="text-blue-600 text-xl hover:bg-gray-100 rounded-full w-[32px] h-[32px] flex items-center justify-center p-3"
                title="Attach image"
              >
                📎
              </button>
            </>

            <textarea
              ref={textareaRef}
              value={messageText}
              onChange={(e) => {
                let value = e.target.value;

                while (value.startsWith("\n")) {
                  value = value.substring(1);
                }

                setMessageText(value);
              }}
              onInput={(e) => {
                const target = e.target;
                const maxRows = 3;
                target.rows = 1;

                const currentRows = target.scrollHeight / 24;
                target.rows = Math.min(Math.floor(currentRows), maxRows);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  sendMessage();
                  e.preventDefault();
                }
              }}
              onPaste={(e) => e.preventDefault()}
              placeholder="Type a message..."
              className="flex-1 px-4 py-2 rounded-full focus:outline-none bg-gray-100 resize-none"
              style={{
                fontSize: '16px',
                touchAction: "manipulation",
                WebkitAppearance: 'none',
                WebkitTapHighlightColor: 'transparent'
              }}
              rows={1}
            />

            <button
              className={`text-blue-600 text-xl hover:bg-gray-100 rounded-full w-[32px] h-[32px] flex items-center justify-center `}
              title={"Hold to Record"}
              onClick={startRecording}
            >
              <BiMicrophone />
            </button>

            <button
              onClick={sendMessage}
              className="bg-[#000] text-white p-2 rounded-full ml-2 md:text-base text-1xl"
            >
              <IoIosSend />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
